import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { Spin } from 'antd';
import { Transactions } from '../../../interfaces/Transactions';
import { mutateConfig } from '../../../api/queryBase';
import {
    formatCurrency,
    getErrorMessage,
    openNotification
} from '../../../utils/helper';
import Button from '../../../components/button/Button';
import { WalletSelect } from '../../../components/WalletSelect';
import CustomModal from '../../../components/CustomModal';
import { FxTransactionTypes, getFXRate } from '../../../api/wallets/wallets';
import Input from '../../../components/input/Input';
import { RefreshIcon } from '../../../components/icons';

interface ApprovePaymentModalProps {
    open: boolean;
    onClose: () => void;
    selected: Transactions;
}

const ApprovePaymentModal = ({
    open,
    onClose,
    selected
}: ApprovePaymentModalProps) => {
    const queryClient = useQueryClient();

    const mutation = mutateConfig({ method: 'post' });

    const { mutate: approveTransaction, isLoading: isApproving } = mutation({
        url: `/transactions/approve/${selected.transactionReference}`,
        cacheKeys: 'approvals'
    });

    const { mutate: rejectTransaction, isLoading: isRejecting } = mutation({
        url: `/transactions/reject/${selected.transactionReference}`,
        cacheKeys: 'approvals'
    });

    const [loadingRate, setLoadingRate] = useState(false);
    const [newRate, setNewRate] = useState(0);

    const getRate = useCallback(async () => {
        try {
            setLoadingRate(true);
            if (selected.fromCurrencyCode === selected.toCurrencyCode) {
                setNewRate(1);
                setLoadingRate(false);
            } else {
                const res = await getFXRate({
                    currencyFrom: selected.fromCurrencyCode,
                    currencyTo: selected.toCurrencyCode,
                    type:
                        selected.transactionType === 1
                            ? FxTransactionTypes.WALLET_CONVERSION
                            : selected.transactionType === 2
                            ? FxTransactionTypes.BENEFICIARY_FUNDING_DIRECT
                            : FxTransactionTypes.BENEFICIARY_FUNDING_CONVERSION,
                    amountToSend: selected.amountFrom
                });
                const { rate } = res.data.data;

                setNewRate(rate);
                setLoadingRate(false);
            }
        } catch (error) {
            setLoadingRate(false);
            if (error?.response) {
                openNotification('error', 'Get rate, Something went wrong');
            }
        }
    }, [selected]);

    useEffect(() => {
        getRate();
    }, [selected, getRate]);

    const handleApprove = () => {
        approveTransaction(
            { expectedRate: newRate },
            {
                onSettled: (_, error: AxiosError | undefined | any) => {
                    if (error) {
                        toast.error(
                            getErrorMessage(error) || 'An error occurred.'
                        );
                        return;
                    }
                    queryClient.invalidateQueries('approvals');
                    toast.success('Transaction approved.');
                    onClose();
                }
            }
        );
    };

    const handleReject = () => {
        rejectTransaction(
            { expectedRate: newRate },
            {
                onSettled: (_, error: AxiosError | undefined | any) => {
                    if (error) {
                        toast.error(
                            getErrorMessage(error) || 'An error occurred.'
                        );
                        return;
                    }
                    queryClient.invalidateQueries('approvals');
                    toast.success('Transaction rejected.');
                    onClose();
                }
            }
        );
    };

    return (
        <CustomModal isModalOpen={open} onClose={onClose} title=''>
            <div className='flex flex-col gap-[24px]'>
                <Input
                    label='Beneficiary Name'
                    value={selected.corporateBeneficiaryName || '-'}
                    disabled
                />

                <Input
                    label='Amount From'
                    value={formatCurrency(
                        selected.amountFrom,
                        selected.fromCurrencyCode
                    )}
                    disabled
                />

                <div className='flex gap-4 w-full'>
                    <div className=' w-full'>
                        <Input
                            label='Initial Conversion Rate'
                            value={`${selected.toCurrencyCode} ${selected.rateValue}`}
                            disabled
                            className='w-full'
                        />
                    </div>

                    <div className=' w-full'>
                        <Input
                            label='Current Conversion Rate'
                            value={`${selected.toCurrencyCode} ${newRate}`}
                            disabled
                            className='w-full'
                        />
                    </div>
                </div>

                <div className='flex justify-between mt-[-12px]'>
                    <div />
                    {loadingRate ? (
                        <Spin />
                    ) : (
                        <button
                            onClick={getRate}
                            className='flex text-appcolorprimary items-center gap-2 text-xs'
                        >
                            <RefreshIcon /> Refresh rate
                        </button>
                    )}
                </div>

                <div>
                    <WalletSelect
                        value={selected.fromWalletId}
                        onHandleChange={() => {}}
                        disabled
                    />
                </div>

                <Input
                    label='Amount To'
                    value={formatCurrency(
                        selected.amountTo,
                        selected.toCurrencyCode
                    )}
                    disabled
                />

                <div>
                    <Button
                        text='Approve'
                        onClick={handleApprove}
                        // fullWidth
                        isLoading={isApproving}
                        disabled={isApproving || isRejecting || loadingRate}
                        className='mb-2 w-full'
                    />
                    <Button
                        text='Reject'
                        onClick={handleReject}
                        // variant='danger-secondary'
                        isLoading={isRejecting}
                        disabled={isApproving || isRejecting || loadingRate}
                        // fullWidth
                        variant='errorOutline'
                        className='mb-2 w-full'
                    />
                </div>
            </div>
        </CustomModal>
    );
};

export default ApprovePaymentModal;
